// store/courseStore.ts
import { create } from 'zustand';
import { courseService } from '../services/courseService';
import type { Course, Topic, Subject, Chapter, ExploreVideo } from '../types/course';

interface LastPlayedLesson {
  videoId: string;
  videoTitle: string;
  chapterId: string;
  chapterName: string;
  subjectId: string;
  subjectName: string;
}

interface CourseState {
  courses: Course[];
  currentTopic: Topic | null;
  currentSubject: Subject | null;
  currentChapter: Chapter | null;
  lastPlayedLesson: LastPlayedLesson | null;
  isLoading: boolean;
  error: string | null;
  fetchCourses: () => Promise<void>;
  selectTopic: (topicId: string) => Promise<void>;
  selectSubject: (subjectId: string) => Promise<void>;
  selectChapter: (chapterId: string) => Promise<void>;
  resetNavigation: () => void;
  setLastPlayedLesson: (lesson: LastPlayedLesson) => void;
  getAllSubjects: () => Subject[];
  getAllVideosFlat: () => ExploreVideo[];
  getNextLesson: (currentVideoId: string, chapterId: string) => ExploreVideo | null;
}

// Persist last played to localStorage
const LAST_PLAYED_KEY = 'cn_last_played';
const loadLastPlayed = (): LastPlayedLesson | null => {
  try {
    const raw = localStorage.getItem(LAST_PLAYED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const useCourseStore = create<CourseState>((set, get) => ({
  courses: [],
  currentTopic: null,
  currentSubject: null,
  currentChapter: null,
  lastPlayedLesson: loadLastPlayed(),
  isLoading: false,
  error: null,

  fetchCourses: async () => {
    set({ isLoading: true, error: null });
    try {
      const courses = await courseService.getAllCourses();
      set({ courses, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to fetch courses', 
        isLoading: false 
      });
    }
  },

  selectTopic: async (topicId: string) => {
    set({ isLoading: true, error: null });
    try {
      const topic = await courseService.getTopicWithSubjects(topicId);
      set({ currentTopic: topic, currentSubject: null, currentChapter: null, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to load topic', 
        isLoading: false 
      });
    }
  },

  selectSubject: async (subjectId: string) => {
    set({ isLoading: true, error: null });
    try {
      if (get().currentTopic?.subjects) {
        const subject = get().currentTopic?.subjects?.find((s: Subject) => s.id === subjectId) || null;
        set({ currentSubject: subject, currentChapter: null, isLoading: false });
      }
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to load subject', 
        isLoading: false 
      });
    }
  },

  selectChapter: async (chapterId: string) => {
    set({ isLoading: true, error: null });
    try {
      const chapter = await courseService.getChapterWithVideos(chapterId);
      set({ currentChapter: chapter, isLoading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err.message : 'Failed to load chapter', 
        isLoading: false 
      });
    }
  },

  resetNavigation: () => {
    set({ currentTopic: null, currentSubject: null, currentChapter: null });
  },

  setLastPlayedLesson: (lesson: LastPlayedLesson) => {
    localStorage.setItem(LAST_PLAYED_KEY, JSON.stringify(lesson));
    set({ lastPlayedLesson: lesson });
  },

  // Get all subjects across all courses, flattened from topics
  getAllSubjects: (): Subject[] => {
    const { courses } = get();
    const subjects: Subject[] = [];
    for (const course of courses) {
      for (const topic of course.topics || []) {
        for (const subject of topic.subjects || []) {
          subjects.push(subject);
        }
      }
    }
    return subjects;
  },

  // Get all videos flattened for Explore mode
  getAllVideosFlat: (): ExploreVideo[] => {
    const { courses } = get();
    const videos: ExploreVideo[] = [];
    for (const course of courses) {
      for (const topic of course.topics || []) {
        for (const subject of topic.subjects || []) {
          for (const chapter of subject.chapters || []) {
            for (const video of chapter.videos || []) {
              videos.push({
                ...video,
                subjectName: subject.name,
                chapterName: chapter.name,
                subjectColor: subject.color,
              });
            }
          }
        }
      }
    }
    // Shuffle for explore discovery
    for (let i = videos.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [videos[i], videos[j]] = [videos[j], videos[i]];
    }
    return videos;
  },

  // Get next lesson in current chapter
  getNextLesson: (currentVideoId: string, chapterId: string): ExploreVideo | null => {
    const { courses } = get();
    for (const course of courses) {
      for (const topic of course.topics || []) {
        for (const subject of topic.subjects || []) {
          for (const chapter of subject.chapters || []) {
            if (chapter.id === chapterId) {
              const videos = chapter.videos || [];
              const currentIndex = videos.findIndex(v => v.id === currentVideoId);
              if (currentIndex >= 0 && currentIndex < videos.length - 1) {
                const next = videos[currentIndex + 1];
                return {
                  ...next,
                  subjectName: subject.name,
                  chapterName: chapter.name,
                  subjectColor: subject.color,
                };
              }
            }
          }
        }
      }
    }
    return null;
  },
}));
